import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { submitNPS, dismissNPS, checkPendingNPS } from '../../actions/nps-rating';
import styles from './NPSPrompt.module.scss';

const SCORE_COLORS = {
  detractor: styles.scoreButtonDetractor,
  passive: styles.scoreButtonPassive,
  promoter: styles.scoreButtonPromoter
};

function getScoreCategory(score) {
  if (score <= 6) return 'detractor';
  if (score <= 8) return 'passive';
  return 'promoter';
}

function getSessionKey(checkpoint, courseId) {
  return 'nps_dismissed_' + checkpoint + '_' + courseId;
}

class NPSPrompt extends Component {
  constructor(props) {
    super(props);
    this.state = {
      step: props.partial ? 2 : 1, // Step 2 = first follow-up
      npsScore: props.existingScore,
      followUpAnswers: {},
      exiting: false,
      otherTexts: {}
    };
    this.autoDismissTimer = null;
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.submitted && this.props.submitted) {
      const thankYouStep = this.getTotalSteps() + 1;
      this.setState({ step: thankYouStep });
      this.autoDismissTimer = setTimeout(() => {
        this.handleExit();
      }, 2500);
    }
    if (!prevProps.partial && this.props.partial) {
      this.setState({ step: 2, npsScore: this.props.existingScore });
    }
  }

  componentWillUnmount() {
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
    }
  }

  handleExit = () => {
    this.setState({ exiting: true });
    setTimeout(() => {
      const { checkpoint, courseId } = this.props;
      if (checkpoint && courseId) {
        sessionStorage.setItem(getSessionKey(checkpoint, courseId), 'true');
      }
      this.props.dismissNPSAction(this.props.token, {
        checkpoint: this.props.checkpoint,
        course_id: this.props.courseId
      });
    }, 300);
  };

  handleScoreSelect = (score) => {
    this.setState({ npsScore: score });
  };

  handleFollowUpChange = (key, value) => {
    this.setState((prev) => ({
      followUpAnswers: {
        ...prev.followUpAnswers,
        [key]: value
      }
    }));
  };

  handleMultiselectToggle = (key, option) => {
    this.setState((prev) => {
      const current = prev.followUpAnswers[key] || [];
      const updated = current.includes(option) ? current.filter((item) => item !== option) : [...current, option];
      return {
        followUpAnswers: {
          ...prev.followUpAnswers,
          [key]: updated
        }
      };
    });
  };

  handleOtherTextChange = (key, text) => {
    this.setState((prev) => ({
      otherTexts: {
        ...prev.otherTexts,
        [key]: text
      }
    }));
  };

  getTotalSteps = () => {
    const { followUps } = this.props;
    // Step 1 = NPS score, then one step per follow-up, then thank you
    return 1 + (followUps ? followUps.length : 0);
  };

  handleNext = () => {
    const totalSteps = this.getTotalSteps();
    const { step } = this.state;
    if (step < totalSteps) {
      this.setState({ step: step + 1 });
    } else {
      this.handleSubmit();
    }
  };

  handleBack = () => {
    this.setState((prev) => ({ step: Math.max(1, prev.step - 1) }));
  };

  handleSubmit = () => {
    const { token, checkpoint, courseId } = this.props;
    const { npsScore, followUpAnswers, otherTexts } = this.state;

    const processedAnswers = { ...followUpAnswers };
    Object.keys(otherTexts).forEach((key) => {
      if (otherTexts[key] && processedAnswers[key] && Array.isArray(processedAnswers[key])) {
        if (processedAnswers[key].includes('Other')) {
          processedAnswers[key] = processedAnswers[key].filter((item) => item !== 'Other');
          processedAnswers[key].push('Other: ' + otherTexts[key]);
        }
      }
    });

    this.props.submitNPSAction(token, {
      checkpoint: checkpoint,
      course_id: courseId,
      nps_score: npsScore,
      follow_up_answers: processedAnswers
    });
  };

  handleNotNowStep2 = () => {
    const { token, checkpoint, courseId } = this.props;
    const { npsScore } = this.state;

    this.props.submitNPSAction(token, {
      checkpoint: checkpoint,
      course_id: courseId,
      nps_score: npsScore,
      follow_up_answers: {},
      partial: true
    });

    this.handleExit();
  };

  handleRetry = () => {
    this.props.checkPendingNPSAction(this.props.token);
  };

  renderScoreButtons() {
    const { npsScore } = this.state;
    const buttons = [];

    for (let i = 0; i <= 10; i++) {
      const category = getScoreCategory(i);
      const isSelected = npsScore === i;
      const classNames = [styles.scoreButton, SCORE_COLORS[category], isSelected ? styles.selected : ''].filter(Boolean).join(' ');

      buttons.push(
        <button key={i} type="button" className={classNames} onClick={() => this.handleScoreSelect(i)}>
          {i}
        </button>
      );
    }

    return buttons;
  }

  renderFollowUp(followUp, index) {
    const key = followUp.key || 'followup_' + index;
    const value = this.state.followUpAnswers[key];

    switch (followUp.type) {
      case 'scale':
        return this.renderScaleFollowUp(followUp, key, value);
      case 'multiselect':
        return this.renderMultiselectFollowUp(followUp, key, value);
      case 'text':
        return this.renderTextFollowUp(followUp, key, value);
      case 'choice':
        return this.renderChoiceFollowUp(followUp, key, value);
      default:
        return null;
    }
  }

  renderScaleFollowUp(followUp, key, value) {
    const min = followUp.min || 1;
    const max = followUp.max || 5;
    const labels = followUp.labels || {};
    const buttons = [];

    for (let i = min; i <= max; i++) {
      const isSelected = value === i;
      const classNames = [styles.scaleButton, isSelected ? styles.scaleSelected : ''].filter(Boolean).join(' ');
      const label = labels[String(i)] || String(i);

      buttons.push(
        <button key={i} type="button" className={classNames} onClick={() => this.handleFollowUpChange(key, i)}>
          {i} — {label}
        </button>
      );
    }

    return (
      <div className={styles.followUpGroup} key={key}>
        <div className={styles.followUpLabel}>{followUp.question}</div>
        <div className={styles.scaleColumn}>{buttons}</div>
      </div>
    );
  }

  renderMultiselectFollowUp(followUp, key, value) {
    const selected = value || [];
    const options = followUp.options || [];
    const hasOther = followUp.has_other;
    const otherSelected = selected.includes('Other');

    return (
      <div className={styles.followUpGroup} key={key}>
        <div className={styles.followUpLabel}>{followUp.question}</div>
        <div className={styles.checkboxGroup}>
          {options.map((option) => (
            <label key={option} className={styles.checkboxItem}>
              <input type="checkbox" checked={selected.includes(option)} onChange={() => this.handleMultiselectToggle(key, option)} />
              {option}
            </label>
          ))}
          {hasOther && (
            <>
              <label className={styles.checkboxItem}>
                <input type="checkbox" checked={otherSelected} onChange={() => this.handleMultiselectToggle(key, 'Other')} />
                Other
              </label>
              {otherSelected && (
                <input
                  type="text"
                  className={styles.otherInput}
                  placeholder="Please specify..."
                  value={this.state.otherTexts[key] || ''}
                  onChange={(e) => this.handleOtherTextChange(key, e.target.value)}
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  renderTextFollowUp(followUp, key, value) {
    return (
      <div className={styles.followUpGroup} key={key}>
        <div className={styles.followUpLabel}>{followUp.question}</div>
        <textarea
          className={styles.textarea}
          placeholder={followUp.placeholder || 'Your feedback...'}
          value={value || ''}
          onChange={(e) => this.handleFollowUpChange(key, e.target.value)}
        />
      </div>
    );
  }

  renderChoiceFollowUp(followUp, key, value) {
    const options = followUp.options || [];

    return (
      <div className={styles.followUpGroup} key={key}>
        <div className={styles.followUpLabel}>{followUp.question}</div>
        <div className={styles.radioGroup}>
          {options.map((option) => (
            <label key={option} className={styles.radioItem}>
              <input type="radio" name={key} checked={value === option} onChange={() => this.handleFollowUpChange(key, option)} />
              {option}
            </label>
          ))}
        </div>
      </div>
    );
  }

  renderStep1() {
    const { npsQuestion, followUps } = this.props;
    const { npsScore } = this.state;
    const hasFollowUps = followUps && followUps.length > 0;
    const buttonLabel = hasFollowUps ? 'Next \u2192' : 'Submit';

    return (
      <div className={styles.stepContent}>
        <div className={styles.question}>{npsQuestion}</div>
        <div className={styles.scoreContainer}>{this.renderScoreButtons()}</div>
        <div className={styles.scaleLabels}>
          <span className={styles.scaleLabel}>Not at all likely</span>
          <span className={styles.scaleLabel}>Extremely likely</span>
        </div>
        <div className={styles.buttonRow}>
          <button type="button" className={styles.secondaryButton} onClick={this.handleExit}>
            Not now
          </button>
          <button type="button" className={styles.primaryButton} disabled={npsScore === null} onClick={this.handleNext}>
            {buttonLabel}
          </button>
        </div>
      </div>
    );
  }

  renderFollowUpStep(followUpIndex) {
    const { followUps } = this.props;
    const followUp = followUps[followUpIndex];
    const totalSteps = this.getTotalSteps();
    const isLastFollowUp = this.state.step === totalSteps;
    const buttonLabel = isLastFollowUp ? 'Submit' : 'Next \u2192';

    return (
      <div className={styles.stepContent}>
        {this.renderFollowUp(followUp, followUpIndex)}
        <div className={styles.buttonRow}>
          <button type="button" className={styles.secondaryButton} onClick={this.handleBack}>
            {'\u2190 Back'}
          </button>
          <button type="button" className={styles.secondaryButton} onClick={this.handleNotNowStep2}>
            Not now
          </button>
          <div className={styles.buttonRowRight}>
            <button type="button" className={styles.primaryButton} onClick={isLastFollowUp ? this.handleSubmit : this.handleNext}>
              {buttonLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderStep3() {
    return (
      <div className={styles.thankYouContent}>
        <div className={styles.checkmark}>
          <svg viewBox="0 0 24 24">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <div className={styles.thankYouText}>Thank you for your feedback!</div>
      </div>
    );
  }

  renderError() {
    return (
      <div className={styles.errorContent}>
        <div className={styles.errorText}>Something went wrong. Please try again.</div>
        <button type="button" className={styles.primaryButton} onClick={this.handleRetry}>
          Retry
        </button>
      </div>
    );
  }

  render() {
    const { pending, dismissed, submitted, error, checkpoint, courseId } = this.props;
    const { step, exiting } = this.state;

    if (!pending && !submitted) return null;
    if (dismissed && !submitted && step !== 3) return null;

    if (checkpoint && courseId) {
      const sessionKey = getSessionKey(checkpoint, courseId);
      if (sessionStorage.getItem(sessionKey) === 'true') return null;
    }

    const wrapperClass = [styles.wrapper, exiting ? styles.wrapperExiting : ''].filter(Boolean).join(' ');

    const totalSteps = this.getTotalSteps();
    const thankYouStep = totalSteps + 1;

    let content;
    if (error && step !== thankYouStep) {
      content = this.renderError();
    } else if (step === 1) {
      content = this.renderStep1();
    } else if (step > 1 && step <= totalSteps) {
      // Follow-up steps: step 2 = followUp[0], step 3 = followUp[1], etc.
      content = this.renderFollowUpStep(step - 2);
    } else if (step === thankYouStep) {
      content = this.renderStep3();
    }

    return (
      <div className={wrapperClass}>
        <div className={styles.card}>{content}</div>
      </div>
    );
  }
}

NPSPrompt.propTypes = {
  pending: PropTypes.bool.isRequired,
  partial: PropTypes.bool.isRequired,
  checkpoint: PropTypes.string,
  courseId: PropTypes.number,
  courseName: PropTypes.string,
  npsQuestion: PropTypes.string,
  followUps: PropTypes.array,
  existingScore: PropTypes.number,
  dismissed: PropTypes.bool.isRequired,
  submitted: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  token: PropTypes.string.isRequired,
  submitNPSAction: PropTypes.func.isRequired,
  dismissNPSAction: PropTypes.func.isRequired,
  checkPendingNPSAction: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  pending: state.nps.pending,
  partial: state.nps.partial,
  checkpoint: state.nps.checkpoint,
  courseId: state.nps.courseId,
  courseName: state.nps.courseName,
  npsQuestion: state.nps.npsQuestion,
  followUps: state.nps.followUps,
  existingScore: state.nps.existingScore,
  dismissed: state.nps.dismissed,
  submitted: state.nps.submitted,
  error: state.nps.error,
  token: state.user.token
});

export default connect(mapStateToProps, {
  submitNPSAction: submitNPS,
  dismissNPSAction: dismissNPS,
  checkPendingNPSAction: checkPendingNPS
})(NPSPrompt);
